//v1.0 - Pixi loader for loading PIXI resources into
import { Loader } from './Loader';

export class PixiLoader extends Loader {
	public resources: Array<any> = [{ ref: 'spritesheet', path: '/img/work/spritesheetspritesheet.json' }];
	public backgroundResources: Array<any> = null;
	public onResourcesReady: CallableFunction;
	public onBackgroundResourcesReady: CallableFunction;
	public onProgressBackground: Function;
	public backgroundLoader: boolean = false;
	public isFullyComplete: boolean = false; //Set to true when init + background loading is complete

	protected loaded: number = 0;
	//Legacy v5 loader path — dead in practice (_pixiVersion is hard-set to 8 and the
	//runtime v8 CDN build has no PIXI.Loader). Typed any, matching the any-based
	//ambient PIXI.Loader declaration (src/types/globals.d.ts):
	private _loader: any;
	private _pixiVersion = 8;

	private _loading: boolean = false;

	//Load all the resources via the built in PIXI loader:
	public load(): void {
		if (this._loading) {
			return;
		}

		if (this._pixiVersion >= 8) {
			this._loadBundlesPixiV8();
			this._loading = true;
			return;
		}

		//PIXI >=v5
		this._loader = new PIXI.Loader();

		//PIXI >=v8

		//PIXI < v5

		this.resources.forEach((resource) => {
			this._loader.add(resource.ref, resource.path);
		});

		this._loader.onLoad.add((loader, resources) => this.onResourceLoaded(loader, resources));
		this._loader.onComplete.add((loader, resources) => this._onComplete(loader, resources));
		this._loader.onError.add((loader, data) => this._onError()); // called once per errored file
		this._loader.load();
	}

	//Check total assets
	//Spritesheets count for x2 assets as there is JSON and Image to load
	public getTotalAssets(): number {
		let count = 0;
		for (var i = 0; i < this.resources.length; i++) {
			let add: number = this.resources[i].isSpriteSheet ? 2 : 1;
			count += add;
		}

		return count;
	}

	public getLoadedAssets(): number {
		return this.loaded;
	}

	//private
	//--------------------------------------------------------
	//--------------------------------------------------------
	//--------------------------------------------------------
	//--------------------------------------------------------

	//Single resource has loaded so update the progress:
	protected onResourceLoaded(loader: any, data: any): void {
		let totalAssets = this.getTotalAssets();
		this.loaded = Math.round((loader.progress / 100) * totalAssets);

		let loadedAssets = this.getLoadedAssets();
		let ratio = loadedAssets / totalAssets;

		this.progress({
			id: this.id,
			ratio: ratio,
			total: totalAssets,
			loaded: loadedAssets
		});
	}

	private async _loadBundlesPixiV8() {
		//Create the mainifest, this will be all the resources that the PIXI app can load
		await this._createPixiV8Mainfest();

		//Loadin the init style assets. These will be sprite sheets that factor into the
		await this._loadBundle('spritesheets-init');

		if (this.backgroundResources) {
			await this._loadBundle('spritesheets-background', true);
		}

		this.isFullyComplete = true;
	}

	private async _loadBundle(bundleName: string, background: boolean = false) {
		//Load next bundle:

		const bundle = await PIXI.Assets.loadBundle(bundleName, (progress: number) => {
			this.loaded = Math.floor(this.resources.length * progress);

			if (!background) {
				this.progress({
					id: this.id,
					ratio: progress,
					total: this.resources.length,
					loaded: this.loaded
				});
			}
		});

		this._onComplete(null, bundle, bundleName, background);
	}

	private async _createPixiV8Mainfest() {
		let spritesheetsAssets = this.resources.map((resource) => {
			return {
				alias: resource.ref,
				src: resource.path
			};
		});

		let spritesheetBundle = {
			name: 'spritesheets-init',
			assets: spritesheetsAssets
		};

		let bundles = [spritesheetBundle];

		if (this.backgroundResources) {
			let spritesheetsAssetsBackground = this.backgroundResources.map((resource) => {
				return {
					alias: resource.ref,
					src: resource.path
				};
			});

			let spritesheetBundleBackground = {
				name: 'spritesheets-background',
				assets: spritesheetsAssetsBackground
			};

			bundles.push(spritesheetBundleBackground);
		}

		let manifest = {
			bundles: bundles
		};

		if (!this.backgroundLoader) {
			await PIXI.Assets.init({ manifest: manifest });
		}
	}

	//Loader is 100% complete so flag complete
	//passId optional: the legacy v5 onComplete callback passes only (loader, resources),
	//and the body already falls back with `passId || this.id`:
	private _onComplete(loader, resources, passId?, background: boolean = false): void {
		if (!background) {
			this.loaded = this.resources.length;
			this.complete(this.id);
		}

		if (background && this.onBackgroundResourcesReady) {
			this.onBackgroundResourcesReady(passId || this.id, resources);
		} else if (!background && this.onResourcesReady) {
			this.onResourcesReady(passId || this.id, resources);
		}
	}

	//Error callback
	private _onError(): void {
		this.error(`PIXI LOADING ERROR LOADER ID = ${this.id}}`);
	}
}
